import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import { toast } from './paToast';
import { errorAlert, trimString } from './utils';

interface QueryMeta extends Record<string, unknown> {
  suppressErrorToast?: boolean;
  errorMessage?: string;
}

interface MutationMeta extends Record<string, unknown> {
  suppressErrorToast?: boolean;
  errorMessage?: string;
}

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: HTTPError;
    queryMeta: QueryMeta;
    mutationMeta: MutationMeta;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } },
  queryCache: new QueryCache({
    onError: async (error, query) => {
      if (query.meta?.suppressErrorToast) return;
      if (query.meta?.errorMessage) {
        toast.error(trimString(query.meta.errorMessage as string, 500), {
          toastId: query.meta.errorMessage,
        });
        return;
      }
      await errorAlert(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: async (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.suppressErrorToast) return;
      if (mutation.meta?.errorMessage) {
        toast.error(trimString(mutation.meta.errorMessage as string, 500), {
          toastId: mutation.meta.errorMessage,
        });
        return;
      }
      await errorAlert(error);
    },
  }),
});

export const objectToQueryKey = (o: object | undefined): Array<string> =>
  Object.entries(o ?? {}).flatMap(([k, v]) => {
    if (typeof v === 'object' && v !== null) {
      return objectToQueryKey(v);
    }

    return `${k}=${v}`;
  });

// for tanstack query devtools
// declare global {
//   interface Window {
//     __TANSTACK_QUERY_CLIENT__: import('@tanstack/query-core').QueryClient;
//   }
// }
// window.__TANSTACK_QUERY_CLIENT__ = queryClient;
